// ─── HELPERS & UTILITIES ─────────────────────────────────────────────
import React from 'react';
import { IconProperties } from '../icons.properties';
import { sb } from './db';

export function toast(msg, type = "info") {
  try {
    window.dispatchEvent(new CustomEvent("fb-toast", { detail: { msg, type } }));
  } catch (_) { /* ignore if window unavailable */ }
}

export const INR = n => "₹" + Number(n || 0).toLocaleString("en-IN");

/** Compact ₹ for tight mobile stat cards — keeps amounts on one line. */
export const INRShort = (n) => {
  const num = Number(n || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(1).replace(/\.0$/, "")}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(1).replace(/\.0$/, "")}L`;
  if (abs >= 1e3) return `${sign}₹${Math.round(abs / 1e3)}K`;
  return INR(num);
};

/** Dashboard: full format desktop, compact on narrow viewports when requested. */
export const INRDashboard = (n, compact = false) => (compact ? INRShort(n) : INR(n));
export const initials = name => (name || "").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
export const today = () => new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
export const generateTempPassword = () => "FB-" + Math.random().toString(36).substring(2, 8).toUpperCase();

export const isTaskOverdue = t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed" && t.status !== "cancelled";

export function invoiceStatus(inv) {
  const s = inv?.status || "unpaid";
  if ((s === "unpaid" || s === "partial") && inv?.dueDate) {
    const d = new Date(inv.dueDate);
    if (!isNaN(d) && d < new Date(new Date().toDateString())) return "overdue";
  }
  return s;
}

export function dateInRange(dateStr, from, to) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (from && d < new Date(from)) return false;
  if (to && d > new Date(to)) return false;
  return true;
}

export function shouldPersistAudit(entry = {}) {
  if (entry.severity === 'error' || entry.severity === 'failure') return true;
  if (entry.level === 'error') return true;
  const action = String(entry.action || '').toLowerCase();
  if (/(?:^|_)(failed|failure|error|denied|rejected|invalid|validation_failed|unauthorized|forbidden)(?:_|$)/i.test(action)) return true;
  const details = entry.details;
  if (typeof details === 'string' && /error|fail|invalid|denied|rejected|validation/i.test(details)) return true;
  if (details && typeof details === 'object' && (details.error || details.validation || details.failed || details.validationError)) return true;
  return false;
}

/** Write audit_log only for errors / validation failures / failures (saves Cosmos RU). */
export async function logAudit(action, entityType, entity = {}) {
  if (!shouldPersistAudit({ action, ...entity })) return;
  try {
    let actor = {};
    try { actor = JSON.parse(localStorage.getItem("fb_user") || "{}"); } catch (e) {}
    await sb.insert("audit_log", {
      actor_id: actor.id || null,
      actor_name: actor.name || actor.email || "Unknown",
      action, entity_type: entityType,
      entity_id: entity.id != null ? String(entity.id) : null,
      entity_label: entity.label || null,
      company_id: entity.companyId || null,
      details: entity.details || entity.error || null,
      severity: entity.severity || 'error',
    });
  } catch (e) {}
}

export async function logAuditFailure(action, entityType, entity = {}, errorDetail = '') {
  await logAudit(action, entityType, {
    ...entity,
    severity: 'error',
    details: errorDetail || entity.details || entity.error || 'Operation failed',
  });
}

const _notifyDedupe = new Map();

function notifyDedupeKey(uid, { type, title, meta }) {
  const m = meta && typeof meta === "object" ? meta : {};
  return `${uid}|${type}|${title}|${m.taskId || ""}|${m.submissionId || ""}|${m.ticketId || ""}|${m.responseId || ""}|${m.invoiceId || ""}`;
}

async function hasRecentDuplicateNotify(uid, payload, windowMs = 180000) {
  const key = notifyDedupeKey(uid, payload);
  const mem = _notifyDedupe.get(key);
  if (mem && Date.now() - mem < windowMs) return true;

  try {
    const rows = await sb.select("notifications", `user_id=eq.${uid}`);
    const cutoff = Date.now() - windowMs;
    const m = payload.meta && typeof payload.meta === "object" ? payload.meta : {};
    return (rows || []).some(n => {
      if (n.title !== payload.title) return false;
      const created = n.created_at ? Date.parse(n.created_at) : 0;
      if (!Number.isFinite(created) || created < cutoff) return false;
      const nm = parseNotifMeta(n);
      if (m.submissionId && nm?.submissionId === m.submissionId) return true;
      if (m.responseId && nm?.responseId === m.responseId) return true;
      if (m.taskId && nm?.taskId === m.taskId) return true;
      if (m.ticketId && nm?.ticketId === m.ticketId) return true;
      if (m.invoiceId && nm?.invoiceId === m.invoiceId) return true;
      return !m.taskId && !m.submissionId && !m.ticketId && !m.invoiceId;
    });
  } catch {
    return false;
  }
}

export async function notify(userIds, { type = "info", title = "", body = "", icon = "", meta = null } = {}) {
  try {
    const ids = [...new Set((Array.isArray(userIds) ? userIds : [userIds]).filter(Boolean))];
    for (const uid of ids) {
      const payload = { type, title, body, icon, meta };
      if (await hasRecentDuplicateNotify(uid, payload)) continue;
      const key = notifyDedupeKey(uid, payload);
      _notifyDedupe.set(key, Date.now());
      await sb.insert("notifications", {
        user_id: uid,
        type,
        title,
        body,
        icon,
        read: false,
        meta: meta ? JSON.stringify(meta) : null,
      });
    }
  } catch (e) {}
}

/** Resolve client user ids for a company (user_companies + primary contact + email/phone match). */
export function companyUserIds(companyId, userLinks = [], users = [], clients = []) {
  const fromLinks = userLinks.filter(l => l.company_id === companyId).map(l => l.user_id);
  const company = clients.find(c => c.id === companyId);
  // Primary contact (Create Client): portal login user id === company id
  const primary = company ? [companyId] : [];
  const fromContact = users
    .filter(u => u.role === "client" && company && (u.email === company.email || u.phone === company.phone))
    .map(u => u.id);
  return [...new Set([...fromLinks, ...fromContact, ...primary].filter(Boolean))];
}

export async function notifyCompanyUsers(companyId, ctx, payload) {
  const ids = companyUserIds(companyId, ctx.userLinks, ctx.users, ctx.clients);
  if (!ids.length) return;
  await notify(ids, payload);
}

export function getLatestSubmission(submissions, taskId) {
  const subs = (submissions || []).filter(s => s.taskId === taskId);
  if (!subs.length) return null;
  const ts = (s) => {
    const raw = s.createdAt || s.created_at || s.submitted_at || s.submittedAt;
    const n = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(n) ? n : 0;
  };
  return [...subs].sort((a, b) => {
    const diff = ts(b) - ts(a);
    if (diff !== 0) return diff;
    return subs.indexOf(a) - subs.indexOf(b);
  })[0];
}

export function clientNeedsResubmit(submissions, taskId) {
  const sub = getLatestSubmission(submissions, taskId);
  return sub?.status === "changes_requested";
}

export function submissionNeedsReview(sub) {
  return sub?.status === "submitted";
}

export function taskRequiresClientDocs(task) {
  const rt = task?.requirementType;
  return rt === "docs" || rt === "form_docs" || rt === "document";
}

export function clientHasRejectedDocs(tasks, submissions, clientId) {
  return (tasks || []).some(t => {
    if (t.clientId !== clientId) return false;
    return getLatestSubmission(submissions, t.id)?.status === "changes_requested";
  });
}

export function clientHasRequiredUploadTasks(tasks, clientId) {
  return (tasks || []).some(t =>
    t.clientId === clientId &&
    t.status === "client_action" &&
    taskRequiresClientDocs(t)
  );
}

/** Staff (admin/manager/employee) may upload client docs any time after client exists. */
export function canStaffUploadForClient(role) {
  return ["admin", "manager", "employee", "sales"].includes(role);
}

/** Client portal ad-hoc uploads (Documents tab). */
export function canClientUpload({ client, tasks, submissions }) {
  if (!client?.id) return false;
  if (client.uploadEnabled) return true;
  if (clientHasRejectedDocs(tasks, submissions, client.id)) return true;
  if (clientHasRequiredUploadTasks(tasks, client.id)) return true;
  return false;
}

/** Cross-sell: staff manage all; clients cannot remove entries. */
export function canRemoveCrossSell(user, opportunity) {
  if (!user || !opportunity) return false;
  if (user.role === "client") return false;
  return ["admin", "manager", "employee", "sales"].includes(user.role);
}

export function canAddCrossSell(user) {
  if (!user) return false;
  return ["admin", "manager", "employee", "sales", "client"].includes(user.role);
}

export function mapCrossSellOpportunity(row) {
  return {
    id: row.id,
    clientId: row.company_id,
    clientName: row.client_name || "",
    serviceName: row.service_name || "",
    notes: row.notes || "",
    createdBy: row.created_by,
    createdByName: row.created_by_name || "",
    createdAt: row.created_at,
  };
}

/** Client task submit / resubmit modal — only when action pending or reupload after decline. */
export function canClientSubmitTask(task, submission, client) {
  if (!task || task.status === "completed" || task.status === "cancelled") return false;
  if (submission?.status === "changes_requested") return true;
  if (task.status === "team_approval") return false;
  if (task.allowClientEdit && taskRequiresClientDocs(task)) return true;
  if (task.status === "client_action" && taskRequiresClientDocs(task)) return true;
  return false;
}

export function parseJsonField(v, fallback = null) {
  if (v == null) return fallback;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return fallback; }
}

export function docDisplayName(slotLabel, originalName) {
  const label = (slotLabel || "").trim();
  if (!label) return originalName || "document";
  const ext = (originalName || "").includes(".") ? originalName.slice(originalName.lastIndexOf(".")) : "";
  return ext && !label.includes(".") ? `${label}${ext}` : label;
}

export function stripDocExtension(name) {
  return String(name || "").replace(/\.[^.]+$/i, "");
}

/** Parse task upload filename: 1_slotId_timestamp_PAN_Card.pdf → PAN Card */
export function humanizeStorageFileName(raw) {
  const base = String(raw || "").split("/").pop() || "Document";
  const m = base.match(/^\d+_[a-f0-9-]+_\d+_(.+)$/i);
  if (m) return m[1].replace(/_/g, " ");
  return base.replace(/^\d+_/, "");
}

/** Path under company folder is task storage (companyId/taskUuid/...). */
export function isTaskStorageRelativePath(rel) {
  return /^[0-9a-f-]{36}\//i.test(String(rel || ""));
}

export function submissionStatusStyle(status) {
  const map = {
    submitted: { label: "Under review", color: "#7C3AED", bg: "#F5F3FF" },
    approved: { label: "Approved", color: "#16A34A", bg: "#F0FDF4" },
    changes_requested: { label: "Declined — reupload", color: "#DC2626", bg: "#FEF2F2" },
  };
  return map[status] || null;
}

export function deleteRequestStatusStyle(status) {
  const map = {
    pending_approval: { label: "Awaiting admin approval", color: "#92400e", bg: "#fef3c7" },
    approved: { label: "Approved", color: "#166534", bg: "#dcfce7" },
    rejected: { label: "Rejected", color: "#991b1b", bg: "#fee2e2" },
  };
  return map[status] || null;
}

const ENTITY_TYPE_LABELS = {
  tasks: "Task",
  clients: "Client",
  employees: "Employee",
  invoices: "Invoice",
};
export function entityTypeLabel(t) { return ENTITY_TYPE_LABELS[t] || String(t || ""); }

const STAFF_NOTE_ROLES = new Set(["admin", "manager", "employee", "sales"]);

export function makeTaskComment(user, text) {
  const t = String(text || "").trim();
  if (!t || !user?.id) return null;
  return {
    id: uuid(),
    userId: user.id,
    userName: user.name || "Team",
    role: user.role || "employee",
    text: t,
    at: new Date().toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }),
  };
}

/** Notes from admin/employee on a task, plus resubmit instructions after doc review. */
export function getStaffTaskNotes(task, submission) {
  const items = [];
  for (const c of task?.comments || []) {
    if (STAFF_NOTE_ROLES.has(c.role)) {
      items.push({ id: c.id, text: c.text, by: c.userName || "Team", at: c.at, kind: "note" });
    }
  }
  const legacy = String(task?.notes || "").trim();
  if (legacy && !items.some(i => i.text === legacy)) {
    items.push({ id: `legacy-notes-${task?.id}`, text: legacy, by: "Team", at: null, kind: "note" });
  }
  if (submission?.reviewNote && submission.status === "changes_requested") {
    items.push({
      id: `review-${submission.id}`,
      text: submission.reviewNote,
      by: "Team",
      at: submission.reviewedAt,
      kind: "resubmit",
    });
  }
  return items;
}

export function parseNotifMeta(n) {
  if (!n?.meta) return null;
  let v = n.meta;
  for (let i = 0; i < 3; i++) {
    if (v == null) return null;
    if (typeof v === "object") return v;
    try { v = JSON.parse(v); } catch { return null; }
  }
  return typeof v === "object" ? v : null;
}

/** Semantic notification category (ticket, task, invoice…) — not Cosmos entity type. */
export function notifCategory(n) {
  if (!n) return "info";
  const t = n.notif_type || n.notifType;
  if (t && t !== "notifications") return t;
  if (n.type && n.type !== "notifications") return n.type;
  return inferNotifCategoryFromText(n.title, n.body);
}

function inferNotifCategoryFromText(title = "", body = "") {
  const text = `${title} ${body}`.toLowerCase();
  if (/ticket|query|reply on your/.test(text)) return "ticket";
  if (/invoice|billing|payment received|recorded payment/.test(text)) return "invoice";
  if (/payment/.test(text)) return "payment";
  if (/task|submission|approved|changes requested|resubmit/.test(text)) return "task";
  if (/new client|client added/.test(text)) return "client";
  if (/marketplace|service request|service catalog/.test(text)) return "marketplace";
  if (/cross[- ]sell/.test(text)) return "cross-sell";
  if (/document|upload/.test(text)) return "client-docs";
  if (/free resource|gift/.test(text)) return "free-gifts";
  return "info";
}

export function staffUserIds(users, roles = ["admin", "manager", "employee"]) {
  return (users || []).filter(u => roles.includes(u.role)).map(u => u.id);
}

export function notificationVisual(type) {
  const cat = type && type !== "notifications" ? type : "info";
  const map = {
    invoice: { bg: "#EFF6FF", color: "#2563EB", icon: "receipt" },
    payment: { bg: "#F0FDF4", color: "#16A34A", icon: "money" },
    task: { bg: "#FFF7ED", color: "#C2410C", icon: "tasks" },
    ticket: { bg: "#FFF7ED", color: "#C2410C", icon: "tickets" },
    submission: { bg: "#F5F3FF", color: "#7C3AED", icon: "review" },
    approved: { bg: "#F0FDF4", color: "#16A34A", icon: "check" },
    changes_requested: { bg: "#FEF2F2", color: "#DC2626", icon: "undo" },
    client: { bg: "#ECFDF5", color: "#059669", icon: "clients" },
    info: { bg: "#EFF6FF", color: "#2563EB", icon: "bell" },
  };
  return map[cat] || map.info;
}

const NOTIF_MODAL_VIEW = {
  "view-task": "tasks",
  "review-submission": "tasks",
  "client-submit-task": "tasks",
  "view-invoice": "invoices",
  tickets: "tickets",
  clients: "clients",
  marketplace: "marketplace",
  "cross-sell": "cross-sell",
  "free-gifts": "free-gifts",
  "client-docs": "client-docs",
  payments: "payments",
  recurring: "recurring",
  employees: "employees",
  directors: "directors",
};

/** Map logical section / modal key → nav view id for a role. */
export function viewForRole(key, role) {
  const isClient = isClientPortalRole(role);
  const isEmp = role === "employee";
  const map = {
    dashboard: isClient ? "client-home" : isEmp ? "emp-dashboard" : "dashboard",
    tasks: isClient ? "client-tasks" : isEmp ? "emp-tasks" : "tasks",
    "view-task": isClient ? "client-tasks" : isEmp ? "emp-tasks" : "tasks",
    "review-submission": isClient ? "client-tasks" : isEmp ? "emp-tasks" : "tasks",
    "client-submit-task": isClient ? "client-tasks" : isEmp ? "emp-tasks" : "tasks",
    invoices: isClient ? "client-invoices" : "invoices",
    "view-invoice": isClient ? "client-invoices" : "invoices",
    payments: isClient ? "client-invoices" : "payments",
    tickets: "tickets",
    clients: isClient ? "client-home" : isEmp ? "emp-clients" : "clients",
    "client-docs": isClient ? "client-docs" : "clients",
    marketplace: "marketplace",
    "cross-sell": "cross-sell",
    "free-gifts": "free-gifts",
    recurring: "recurring",
    employees: "employees",
    directors: "directors",
    notifications: "notifications",
    analytics: isEmp ? "emp-analytics" : "analytics",
    "billing-summary": "billing-summary",
    reports: "reports",
  };
  return map[key] || key;
}

function inferViewFromNotification(n, role) {
  const meta = parseNotifMeta(n);
  const modal = meta?.modal || meta?.view;
  if (modal) return NOTIF_MODAL_VIEW[modal] || modal;
  const cat = notifCategory(n);
  const byType = {
    invoice: "invoices",
    payment: "payments",
    task: "tasks",
    ticket: "tickets",
    client: "clients",
    submission: "tasks",
    approved: "tasks",
    changes_requested: "tasks",
    marketplace: "marketplace",
    "cross-sell": "cross-sell",
    "free-gifts": "free-gifts",
    "client-docs": role === "client" ? "client-docs" : "clients",
  };
  if (byType[cat]) return byType[cat];
  return null;
}

export async function dismissNotification(id, setNotifications) {
  setNotifications(ns => ns.filter(n => n.id !== id));
  try {
    await sb.delete("notifications", `id=eq.${id}`);
  } catch { /* best-effort */ }
}

/** Route user to the screen/modal for a notification tap (web + Capacitor WebView). */
export function routeFromNotification(n, { setView, openModal, user, tasks, submissions, invoices, setNotifFocus }) {
  const meta = parseNotifMeta(n);
  const role = user?.role || "client";
  const cat = notifCategory(n);
  const modal = meta?.modal || meta?.view;
  const task = meta?.taskId ? (tasks || []).find(t => t.id === meta.taskId) : null;
  const invoice = meta?.invoiceId ? (invoices || []).find(i => i.id === meta.invoiceId) : null;

  const section = inferViewFromNotification(n, role)
    || (role === "client" ? "client-home" : role === "employee" ? "emp-dashboard" : "dashboard");
  setView(viewForRole(section, role));

  if (modal === "view-invoice" || cat === "invoice" || (cat === "payment" && invoice)) {
    if (invoice) openModal("view-invoice", { invoice });
    return;
  }
  if (cat === "payment") return;

  if (modal === "tickets" || cat === "ticket") {
    if (meta?.ticketId && setNotifFocus) setNotifFocus({ ticketId: meta.ticketId });
    return;
  }

  if ((modal === "clients" || cat === "client") && meta?.clientId) {
    if (setNotifFocus) setNotifFocus({ clientId: meta.clientId });
    return;
  }

  if (modal === "client-docs" || section === "client-docs") {
    if (meta?.docsTab && setNotifFocus) setNotifFocus({ docsTab: meta.docsTab });
    return;
  }

  const isTaskFlow = ["submission", "approved", "changes_requested", "task"].includes(cat)
    || ["view-task", "review-submission", "client-submit-task"].includes(modal);
  if (!isTaskFlow) return;

  if (isClientPortalRole(role)) {
    if (task && (modal === "client-submit-task" || cat === "changes_requested" || meta?.action === "submit" || n.title?.toLowerCase().includes("action"))) {
      openModal("client-submit-task", { task });
    }
    return;
  }

  if (modal === "review-submission" || ["submission", "approved", "changes_requested"].includes(cat)) {
    const sub = meta?.submissionId
      ? (submissions || []).find(s => s.id === meta.submissionId)
      : task ? getLatestSubmission(submissions, task.id) : null;
    if (sub && submissionNeedsReview(sub)) openModal("review-submission", { submissionId: sub.id });
    else if (meta?.submissionId) openModal("review-submission", { submissionId: meta.submissionId });
  } else if (task) {
    openModal("view-task", { task });
  }
}

export const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const getIcon = (id, defaultEmoji) => {
  const mapping = {
    "dashboard": "dashboard", "emp-dashboard": "dashboard",
    "analytics": "analytics", "emp-analytics": "employeeAnalytics",
    "billing-summary": "billingSummary", "reports": "reports",
    "clients": "clients", "emp-clients": "clients", "client-home": "clients",
    "invoices": "invoices", "client-invoices": "invoices",
    "payments": "payments", "tasks": "tasks", "emp-tasks": "tasks",
    "client-tasks": "tasks", "recurring": "recurringTasks",
    "tickets": "tickets", "marketplace": "marketplace",
    "cross-sell": "crossSell", "employees": "employees",
    "directors": "directors", "settings-org": "organisation",
    "settings-bundles": "bundles", "settings-doctpls": "templates",
    "settings-users": "usersRoles", "settings-kraya": "kraya", "settings-zoho": "upload",
    "client-docs": "folder", "free-gifts": "gifts",
    "delete-requests": "delete"
  };
  const key = mapping[id];
  const IconComp = IconProperties[key];
  if (IconComp) return React.createElement(IconComp, { size: 16 });
  return defaultEmoji;
};

// ─── DATA MAPPERS ─────────────────────────────────────────────────────
export function rollupClientBilling(clientId, invoices, payments = []) {
  const invs = (invoices || []).filter(i => i.clientId === clientId && i.status !== "void");
  const totalBilling = invs.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const collected = invs.reduce((s, i) => {
    const fin = payments?.length ? resolveInvoiceFinancials(i, payments) : normalizeInvoice(i);
    return s + (Number(fin.paid) || 0);
  }, 0);
  const pending = invs.reduce((s, i) => {
    const fin = payments?.length ? resolveInvoiceFinancials(i, payments) : normalizeInvoice(i);
    const p = Number(fin.pending);
    return s + (Number.isFinite(p) ? p : Math.max(0, (Number(fin.total) || 0) - (Number(fin.paid) || 0)));
  }, 0);
  return { totalBilling, collected, pending };
}

/** Live task completion % — avoids stale companies.progress column. */
export function clientTaskProgress(clientId, tasks, fallback = 0) {
  const clientTasks = (tasks || []).filter(t => t.clientId === clientId);
  if (!clientTasks.length) return Number(fallback) || 0;
  const done = clientTasks.filter(t => t.status === "completed").length;
  return Math.round((done / clientTasks.length) * 100);
}

/** Invoice-derived billing + live task progress for list rows. */
/** Task owner: explicit assignee, else the client's assigned employee (EmpDashboard uses the same rule). */
export function taskEffectiveAssignee(task, clientsOrById) {
  if (!task) return null;
  if (task.assignedTo) return task.assignedTo;
  const client = clientsOrById instanceof Map
    ? clientsOrById.get(task.clientId)
    : Array.isArray(clientsOrById)
      ? clientsOrById.find(c => c.id === task.clientId)
      : null;
  return client?.assignedTo || null;
}

/** All Clients "Assigned To" when company row is empty but every task shares one assignee. */
export function clientDisplayAssignee(client, clientTasks = []) {
  if (client?.assignedTo) return client.assignedTo;
  const ids = [...new Set((clientTasks || []).map(t => t.assignedTo).filter(Boolean))];
  return ids.length === 1 ? ids[0] : null;
}

export function clientMatchesAssignee(client, assigneeId, tasks = [], clients = []) {
  if (!assigneeId) return true;
  if (client?.assignedTo === assigneeId) return true;
  return (tasks || []).some(t =>
    t.clientId === client?.id && taskEffectiveAssignee(t, clients) === assigneeId,
  );
}

/** Tasks owned by an employee: directly assigned or on their assigned clients (EmpDashboard rule). */
export function tasksForEmployee(empId, tasks = [], clients = []) {
  if (!empId) return tasks || [];
  const clientIds = new Set((clients || []).filter(c => c.assignedTo === empId).map(c => c.id));
  return (tasks || []).filter(t => t.assignedTo === empId || clientIds.has(t.clientId));
}

export function enrichClientRow(client, invoices = [], tasks = [], payments = []) {
  if (!client) return client;
  return {
    ...client,
    ...rollupClientBilling(client.id, invoices, payments),
    progress: clientTaskProgress(client.id, tasks, client.progress),
  };
}

export function normalizeInvoice(i) {
  if (!i) return i;
  const total = Number(i.total) || 0;
  const paid = Number(i.paid) || 0;
  let status = i.status || "unpaid";
  if (status === "void") {
    return { ...i, total, paid, pending: 0, status: "void" };
  }
  let pending = Number(i.pending);
  if (!Number.isFinite(pending)) pending = Math.max(0, total - paid);
  else pending = Math.max(0, pending);
  if (status !== "pending_approval") {
    if (pending <= 0 && total > 0 && paid >= total) status = "paid";
    else if (paid > 0 && pending > 0) status = "partial";
    else if (paid <= 0 && pending > 0) status = "unpaid";
  }
  return { ...i, total, paid, pending, status };
}

/** Client portal login (primary contact, linked director, or explicit director role). */
export function isClientPortalRole(role) {
  return role === "client" || role === "director";
}

/** Portal director login — not the primary company contact (user id === company id). */
export function isDirectorUser(u, clients) {
  if (!u) return false;
  if (u.role === "director") return true;
  if (u.role !== "client") return false;
  return !(clients || []).some(c => c.id === u.id);
}

const OFFICE_DOC_EXT = new Set([
  "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp", "rtf",
]);

/** Word/Excel/PowerPoint etc. — browsers cannot render these inline; use Download. */
export function isOfficeDocumentName(fileName = "") {
  const base = String(fileName).split("/").pop() || "";
  const dot = base.lastIndexOf(".");
  if (dot < 0) return false;
  return OFFICE_DOC_EXT.has(base.slice(dot + 1).toLowerCase());
}

export function instalmentFinancials(row) {
  if (!row) return { amount: 0, paid: 0, residual: 0, status: "unpaid" };
  const amount = Number(row.amount) || 0;
  const paid = Number(row.paid_amount ?? row.paidAmount) || 0;
  const residual = Math.max(0, amount - paid);
  let status = row.status || "unpaid";
  if (status !== "pending_review" && residual <= 0.01 && amount > 0) status = "paid";
  else if (status !== "pending_review" && paid > 0 && residual > 0.01) status = "partial";
  return { amount, paid, residual, status };
}

export function nextUnpaidInstallment(scheduleRows) {
  const rows = (scheduleRows || [])
    .map(r => ({ row: r, fin: instalmentFinancials(r) }))
    .filter(({ fin, row }) => fin.residual > 0.01 && row.status !== "pending_review");
  // ponytail: FIFO = seq order; due_date is display-only, not payment order
  rows.sort((a, b) => (a.row.seq || 0) - (b.row.seq || 0));
  return rows[0]?.row || null;
}

export function clientPayable(invoice, scheduleRows) {
  if (invoice?.status === "void") return { amount: 0, scheduleId: null, hasInstallments: false };
  const rows = (scheduleRows || []).filter(r => (r.invoice_id || r.invoiceId) === invoice?.id);
  if (!rows.length) return { amount: invoice?.pending || 0, scheduleId: null, hasInstallments: false };
  const next = nextUnpaidInstallment(rows);
  if (!next) return { amount: 0, scheduleId: null, hasInstallments: true };
  const fin = instalmentFinancials(next);
  return { amount: fin.residual, scheduleId: next.id, hasInstallments: true };
}

/** Apply approved/collected amount to instalment rows (oldest unpaid first, or one scheduleId). */
export async function syncSchedulePayment(sb, invoiceId, amount, scheduleId = null) {
  const schedules = await sb.select("payment_schedule", `invoice_id=eq.${invoiceId}`);
  let remaining = Number(amount) || 0;
  if (remaining <= 0) return;

  const sorted = (schedules || []).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));
  const targets = scheduleId
    ? sorted.filter(s => s.id === scheduleId)
    : sorted.filter(s => instalmentFinancials(s).residual > 0.01 && s.status !== "pending_review");

  for (const row of targets) {
    if (remaining <= 0.01) break;
    const fin = instalmentFinancials(row);
    if (fin.residual <= 0.01) continue;
    const apply = Math.min(remaining, fin.residual);
    const newPaid = fin.paid + apply;
    const newStatus = newPaid >= fin.amount - 0.01 ? "paid" : "partial";
    await sb.update("payment_schedule", "id", row.id, {
      paid_amount: newPaid,
      status: newStatus,
      paid_at: newStatus === "paid" ? new Date().toISOString() : (row.paid_at || null),
    });
    remaining -= apply;
  }
}

/** Redistribute invoice paid total across schedule rows (fixes drift when sync was skipped). */
export async function reconcileInstallmentSchedule(sb, invoiceId, invoice, payments = []) {
  const schedules = await sb.select("payment_schedule", `invoice_id=eq.${invoiceId}`);
  if (!schedules?.length) return;

  const fin = resolveInvoiceFinancials(invoice, payments);
  const schedTotal = schedules.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  let remaining = Math.min(fin.paid, schedTotal);
  const sorted = schedules.slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));

  for (const row of sorted) {
    const amount = Number(row.amount) || 0;
    const apply = Math.min(remaining, amount);
    remaining = Math.max(0, remaining - apply);
    const newStatus = apply >= amount - 0.01 ? "paid" : apply > 0.01 ? "partial"
      : (row.status === "pending_review" ? "pending_review" : "unpaid");
    const curPaid = Number(row.paid_amount ?? row.paidAmount) || 0;
    if (Math.abs(curPaid - apply) > 0.01 || row.status !== newStatus) {
      await sb.update("payment_schedule", "id", row.id, {
        paid_amount: apply,
        status: newStatus,
        paid_at: newStatus === "paid" ? (row.paid_at || new Date().toISOString()) : null,
      });
    }
  }
}

export function resolveInvoiceFinancials(invoice, payments = []) {
  const inv = normalizeInvoice(invoice);
  if (inv.status === "void") return inv;

  const invPayments = (payments || []).filter(p => p.invoiceId === inv.id);
  const approvedSum = invPayments
    .filter(p => p.status === "approved")
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const pendingPay = invPayments.some(p =>
    p.status === "pending_approval" || p.status === "pending_review"
  );

  const total = Number(inv.total) || 0;
  const paid = Math.max(Number(inv.paid) || 0, approvedSum);
  const pending = Math.max(0, total - paid);
  let status = inv.status;

  if (status === "pending_approval" || status === "pending_review") {
    if (!pendingPay && approvedSum > 0) {
      status = pending <= 0 ? "paid" : "partial";
    } else if (pendingPay) {
      status = "pending_approval";
    }
  } else if (status !== "void") {
    status = pending <= 0 && total > 0 ? "paid" : paid > 0 && pending > 0 ? "partial" : status;
  }

  return normalizeInvoice({ ...inv, paid, pending, status });
}

/** Admin approves a client-submitted online payment — syncs invoice, instalment, and roll-ups. */
export async function applyApprovedPayment({
  payment, invoice, sb, setInvoices, setPayments, setClients,
}) {
  const amt = Number(payment.amount) || 0;
  const newPaid = (Number(invoice.paid) || 0) + amt;
  const newPending = Math.max(0, (Number(invoice.total) || 0) - newPaid);
  const newStatus = newPending <= 0 ? "paid" : "partial";

  await sb.update("payments", "id", payment.id, { status: "approved" });
  await sb.update("invoices", "id", invoice.id, { paid: newPaid, pending: newPending, status: newStatus });

  try {
    const schedules = await sb.select("payment_schedule", `invoice_id=eq.${invoice.id}`);
    const pendingReview = (schedules || []).find(s => s.status === "pending_review");
    const scheduleId = payment.scheduleId || payment.schedule_id || pendingReview?.id || null;
    await syncSchedulePayment(sb, invoice.id, amt, scheduleId);
    await reconcileInstallmentSchedule(sb, invoice.id, { ...invoice, paid: newPaid, pending: newPending, status: newStatus }, []);
  } catch { /* instalment sync best-effort */ }

  setPayments?.(ps => ps.map(x => (x.id === payment.id ? { ...x, status: "approved" } : x)));
  setInvoices?.(is => is.map(i => (
    i.id === invoice.id ? { ...i, paid: newPaid, pending: newPending, status: newStatus } : i
  )));
  setClients?.(cs => cs.map(c => {
    if (c.id !== invoice.clientId) return c;
    const collected = (Number(c.collected) || 0) + amt;
    return { ...c, collected, pending: Math.max(0, (Number(c.totalBilling) || 0) - collected) };
  }));

  return { paid: newPaid, pending: newPending, status: newStatus };
}

export const mapClient  = c => ({ id:c.id, clientNo:c.client_no, name:c.name, contactName:c.contact_name, email:c.email, phone:c.phone, type:c.type, status:c.status, assignedTo:c.assigned_to, salesOwner:c.sales_owner||null, totalBilling:Number(c.total_billing)||0, collected:Number(c.collected)||0, pending:Number(c.pending)||0, cin:c.cin||"", state:c.state||"", gst:c.gst||"", pan:c.pan||"", progress:c.progress||0, incorporationDate:c.incorporation_date||"", uploadEnabled:!!c.upload_enabled, customData:parseJsonField(c.custom_data, {}) || {}, createdAt:c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : today() });
export const mapInvoice = i => normalizeInvoice({ id:i.id, invoiceNo:i.invoice_no, clientId:i.company_id, clientName:i.client_name, date:i.date||today(), dueDate:i.due_date, status:i.status||"unpaid", total:Number(i.total)||0, paid:Number(i.paid)||0, pending:Number(i.pending), letterhead:i.letterhead||null, voidReason:i.void_reason||null, voidedAt:i.voided_at||null, lineItems:[] });
export const mapTask    = t => ({ id:t.id, title:t.title, clientId:t.company_id, clientName:t.client_name, invoiceId:t.invoice_id, assignedTo:t.assigned_to, status:t.status||"open", sequence:t.sequence||1, requirementType:t.requirement_type||"none", docTemplateId:t.doc_template_id, directorNumber:t.director_number, directorCount:t.director_count||1, dueDate:t.due_date, completedDate:t.completed_date, notes:t.notes||"", category:t.category, allowClientEdit:!!t.allow_client_edit, isRecurring:t.is_recurring, freq:t.recurring_freq, startDate:t.start_date, endDate:t.end_date, packageId:t.package_id, recurringTemplate:t.package_id, customData:parseJsonField(t.custom_data, {}) || {}, comments:Array.isArray(parseJsonField(t.comments, [])) ? parseJsonField(t.comments, []) : [] });
export const mapTicket  = t => ({ id:t.id, ticketNo:t.ticket_no, clientId:t.company_id, clientName:t.client_name, raisedBy:t.raised_by, assignedTo:t.assigned_to, subject:t.subject, description:t.description||"", priority:t.priority||"medium", status:t.status||"open", tat:t.tat, createdAt:t.created_at ? new Date(t.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : today(), updatedAt:t.updated_at ? new Date(t.updated_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : today(), responses:[] });
export const mapPayment = p => ({ id:p.id, invoiceId:p.invoice_id, clientId:p.company_id, clientName:p.client_name, amount:Number(p.amount)||0, mode:p.mode, reference:p.reference, date:p.date, notes:p.notes, recordedBy:p.recorded_by, status:p.status||"approved", scheduleId:p.schedule_id||p.scheduleId||null });
export const mapSub     = s => ({ id:s.id, taskId:s.task_id, clientId:s.company_id, status:s.status, formData:s.form_data||{}, documents:s.documents||{}, note:s.note, reviewNote:s.review_note, rejectedItems:(Array.isArray(s.rejected_items)?s.rejected_items:(typeof s.rejected_items==="string"?(()=>{try{return JSON.parse(s.rejected_items||"[]")}catch{return[]}})():[])), submittedAt:s.submitted_at, reviewedAt:s.reviewed_at, createdAt:s.created_at, locked:s.locked!==false });
export const mapResp    = r => ({ by:r.by_role, byName:r.by_name, text:r.text, date:r.date });
export const mapNotif   = n => ({ id:n.id, userId:n.user_id || n.userId, type:notifCategory(n), title:n.title, body:n.body, icon:n.icon||"", read:!!n.read, meta:parseNotifMeta(n), time: n.created_at ? new Date(n.created_at).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}) : "" });
